import { useState, useRef, useEffect, type MouseEvent } from "react";
import DolapoTextWithoutUnderline from "./DolapoTextWithoutUnderline";
import { TextAlign, TextStyle } from "../props/enums";
import "../asset/fonts/InriaSans-Regular.ttf";
import "../asset/fonts/Inter-Regular.ttf";
import "../asset/fonts/Jost-Light.ttf";

const menuItems = [
  { label: "Home", id: "home" },
  { label: "Skills", id: "skills" },
  { label: "Companies", id: "companies" },
  { label: "Projects", id: "projects" },
  { label: "Blogs", id: "blogs" },
];

const MenuBar = () => {
  const [indicatorPosition, setIndicatorPosition] = useState(0);
  const [indicatorWidth, setIndicatorWidth] = useState(0);
  const [activeMenuItem, setActiveMenuItem] = useState("home");
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const navRef = useRef<HTMLDivElement>(null);
  const buttonRefs = useRef<Record<string, HTMLButtonElement | null>>({});

  const updateIndicatorForButton = (button: HTMLButtonElement | null) => {
    if (!button || !navRef.current) {
      return;
    }

    const navRect = navRef.current.getBoundingClientRect();
    const buttonRect = button.getBoundingClientRect();

    setIndicatorPosition(buttonRect.left - navRect.left);
    setIndicatorWidth(buttonRect.width);
  };

  useEffect(() => {
    const updateActiveSectionIndicator = () => {
      const sectionIds = menuItems.map((item) => item.id);
      let closestId = activeMenuItem;
      let smallestDistance = Number.POSITIVE_INFINITY;

      sectionIds.forEach((sectionId) => {
        const section = document.getElementById(sectionId);

        if (!section) {
          return;
        }

        const rect = section.getBoundingClientRect();
        const distance = Math.abs(rect.top - 90);

        if (distance < smallestDistance) {
          closestId = sectionId;
          smallestDistance = distance;
        }
      });

      if (closestId !== activeMenuItem) {
        setActiveMenuItem(closestId);
      }

      const activeButton = buttonRefs.current[closestId];
      updateIndicatorForButton(activeButton ?? buttonRefs.current.home);
    };

    updateActiveSectionIndicator();
    window.addEventListener("scroll", updateActiveSectionIndicator, {
      passive: true,
    });
    window.addEventListener("resize", updateActiveSectionIndicator);

    return () => {
      window.removeEventListener("scroll", updateActiveSectionIndicator);
      window.removeEventListener("resize", updateActiveSectionIndicator);
    };
  }, [activeMenuItem]);

  const handleClick = (event: MouseEvent<HTMLButtonElement>, id: string) => {
    const node = event.currentTarget as HTMLButtonElement;
    setActiveMenuItem(id);
    setIsMobileMenuOpen(false);
    updateIndicatorForButton(node);

    const target = document.getElementById(id);
    if (!target) {
      return;
    }

    const offset = 90;
    const top = target.getBoundingClientRect().top + window.scrollY - offset;

    window.scrollTo({
      top,
      behavior: "smooth",
    });
  };

  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth >= 768) {
        setIsMobileMenuOpen(false);
      }
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  return (
    <>
      {isMobileMenuOpen && (
        <button
          type="button"
          aria-label="Close mobile menu"
          onClick={() => setIsMobileMenuOpen(false)}
          className="fixed inset-0 z-40 bg-black/30 md:hidden"
        />
      )}

      <nav className="sticky top-0 z-50 w-full border-b border-white/10 bg-[#2b2b2b]/90 backdrop-blur-sm">
        <div className="mx-auto flex max-w-[1400px] items-center justify-between px-4 py-4 md:px-6">
          <div className="ml-2 text-2xl font-bold text-white md:ml-4 md:text-3xl">
            <DolapoTextWithoutUnderline
              content="Dolapo Olakanmi"
              size={32}
              color="#fff"
              marginBottom={0}
              underline={false}
              fontWeight={TextStyle.BOLD}
              hasMarginBottom={false}
              textalign={TextAlign.CENTER}
            />
          </div>

          <button
            type="button"
            aria-label={isMobileMenuOpen ? "Close menu" : "Open menu"}
            onClick={() => setIsMobileMenuOpen((prev) => !prev)}
            className="relative z-50 flex h-10 w-10 items-center justify-center rounded-md border border-white/10 bg-white/5 md:hidden"
          >
            <span
              className={`absolute block h-0.5 w-6 rounded-full bg-white transition-all duration-300 ${
                isMobileMenuOpen ? "rotate-45" : "-translate-y-2"
              }`}
            />
            <span
              className={`absolute block h-0.5 w-6 rounded-full bg-white transition-all duration-300 ${
                isMobileMenuOpen ? "opacity-0" : "opacity-100"
              }`}
            />
            <span
              className={`absolute block h-0.5 w-6 rounded-full bg-white transition-all duration-300 ${
                isMobileMenuOpen ? "-rotate-45" : "translate-y-2"
              }`}
            />
          </button>

          <div
            ref={navRef}
            className="relative ml-auto hidden items-center gap-6 pr-4 md:flex"
          >
            {menuItems.map((item) => (
              <button
                key={item.id}
                ref={(element) => {
                  buttonRefs.current[item.id] = element;
                }}
                type="button"
                onClick={(event) => handleClick(event, item.id)}
                className="relative rounded-full px-3 py-2 text-sm font-medium text-white transition hover:opacity-70"
              >
                <DolapoTextWithoutUnderline
                  content={item.label}
                  size={18}
                  color="#fff"
                  marginBottom={0}
                  underline={false}
                  fontWeight={TextStyle.NORMAL}
                  hasMarginBottom={false}
                  textalign={TextAlign.CENTER}
                />
              </button>
            ))}

            <div
              className="pointer-events-none absolute -bottom-2 h-2 transition-[left,width,transform] duration-500 ease-[cubic-bezier(0.22,1,0.36,1)] will-change-[left,width]"
              style={{
                left: `${indicatorPosition}px`,
                width: `${indicatorWidth}px`,
                transform: "translateY(0)",
              }}
            >
              <img
                src={require("../asset/image/home-menu-indicator.png")}
                alt="menu indicator"
                className="h-full w-full object-cover"
              />
            </div>
          </div>
        </div>
      </nav>

      <aside
        className={`fixed right-0 top-0 z-50 h-full w-72 max-w-[80vw] transform border-l border-white/10 bg-[#2b2b2b]/95 p-5 shadow-2xl transition-transform duration-300 ease-in-out md:hidden ${
          isMobileMenuOpen ? "translate-x-0" : "translate-x-full"
        }`}
      >
        <div className="mb-6 flex items-center justify-between border-b border-white/10 pb-4">
          <span className="text-lg font-semibold text-white">Menu</span>
          <button
            type="button"
            aria-label="Close menu"
            onClick={() => setIsMobileMenuOpen(false)}
            className="flex h-9 w-9 items-center justify-center rounded-md border border-white/10 text-xl text-white"
          >
            ×
          </button>
        </div>

        <div className="flex flex-col gap-2">
          {menuItems.map((item) => (
            <button
              key={item.id}
              type="button"
              onClick={(event) => handleClick(event, item.id)}
              className={`rounded-xl px-4 py-3 text-left text-base font-medium transition ${
                activeMenuItem === item.id
                  ? "bg-white/10 text-white"
                  : "text-white/80 hover:bg-white/5 hover:text-white"
              }`}
            >
              {item.label}
            </button>
          ))}
        </div>
      </aside>
    </>
  );
};

export default MenuBar;
